import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TourButton from '../../TourButton/TourButton';
import { getRecipePickerModalSteps } from '../../../tours/recipePickerModal';
import axios from 'axios';
import { useAppContext } from '../../../context/AppContext';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import { useMediaQuery } from '@mui/material';
import RecipeCard from '../../RecipeCard/RecipeCard';
import './RecipePickerModal.css';
import { sanitizeUrl } from '../../../utils/functions';

const RECIPE_PAGE_LIMITS = [10, 25, 50, 100];

const RecipePickerModal = ({ open, dietistId, language, onClose, onSelect }) => {
    const { t } = useTranslation();
    const { appColors } = useAppContext();
    const isMobile = useMediaQuery('(max-width: 768px)');

    const [recipes, setRecipes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [limit, setLimit] = useState(RECIPE_PAGE_LIMITS[0]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);

    useEffect(() => {
        if (!open || !dietistId) return;

        const controller = new AbortController();
        const fetchRecipes = async () => {
            try {
                setLoading(true);
                const response = await axios.get(
                    `${process.env.REACT_APP_BACKEND_URL}/dietists/${dietistId}/recipes`,
                    {
                        params: {
                            page,
                            limit,
                            nameFilter: searchQuery,
                            language,
                        },
                        withCredentials: true,
                        signal: controller.signal,
                    }
                );

                const data = response.data ?? {};
                const fetchedRecipes = data.recipes ?? data.data ?? data.docs ?? [];
                const count = data.totalCount ?? data.total ?? data.count ?? data.pagination?.totalCount ?? fetchedRecipes.length;
                const pages = data.totalPages ?? data.pagination?.totalPages ?? Math.max(1, Math.ceil(count / limit));

                setRecipes(Array.isArray(fetchedRecipes) ? fetchedRecipes : []);
                setTotalCount(count);
                setTotalPages(pages || 1);
            } catch (error) {
                if (controller.signal.aborted) return;
                console.error('RecipePickerModal: fetchRecipes error', error);
                setRecipes([]);
                setTotalCount(0);
                setTotalPages(1);
            } finally {
                if (!controller.signal.aborted) {
                    setLoading(false);
                }
            }
        };

        const debounceTimer = setTimeout(() => {
            fetchRecipes();
        }, 250);

        return () => {
            clearTimeout(debounceTimer);
            controller.abort();
        };
    }, [open, dietistId, page, limit, searchQuery, language]);

    if (!open) return null;

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
        setPage(1);
    };

    const handleLimitChange = (e) => {
        setLimit(Number(e.target.value));
        setPage(1);
    };

    const handleSelect = (recipe) => {
        onSelect(recipe);
    };

    const renderPageButtons = () => {
        const buttons = [];
        for (let i = 1; i <= totalPages; i++) {
            buttons.push(
                <button
                    key={i}
                    type='button'
                    className={`rpm-page-btn ${page === i ? 'active' : ''}`}
                    onClick={() => setPage(i)}
                >
                    {i}
                </button>
            );
        }
        return buttons;
    };

    return (
        <div className='rpm-overlay' onClick={onClose} aria-modal='true' role='dialog'>
            <div className='rpm-modal' onClick={(e) => e.stopPropagation()} data-tour="rpm-modal">
                <div className='rpm-header'>
                    <div>
                        <h3 className='rpm-title'>{t('aml-select-recipe-title')}</h3>
                        <p className='rpm-subtitle'>{t('aml-select-recipe-subtitle')}</p>
                    </div>
                    <div className="rpm-tutorial-btn">
                        <TourButton steps={getRecipePickerModalSteps(t)} iconOnly={isMobile} />
                    </div>
                    <button type='button' className='rpm-close-btn' onClick={onClose} aria-label='Close'>
                        <span className='material-symbols-outlined'>close</span>
                    </button>
                </div>

                <div className='rpm-toolbar' data-tour="rpm-toolbar">
                    <TextField
                        size='small'
                        value={searchQuery}
                        onChange={handleSearchChange}
                        placeholder={t('aml-search-recipe-placeholder')}
                        className='rpm-search'
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                '&:hover fieldset': { borderColor: appColors.tertiary },
                                '&.Mui-focused fieldset': { borderColor: appColors.secondary },
                            },
                        }}
                    />

                    <FormControl
                        size='small'
                        className='rpm-limit'
                        sx={{
                            minWidth: 120,
                            '& .MuiOutlinedInput-root': {
                                '&:hover fieldset': { borderColor: appColors.tertiary },
                                '&.Mui-focused fieldset': { borderColor: appColors.secondary },
                            },
                            '& .MuiInputLabel-root.Mui-focused': { color: appColors.secondary },
                        }}
                    >
                        <InputLabel id='rpm-recipe-limit-label'>{t('aml-limit')}</InputLabel>
                        <Select
                            labelId='rpm-recipe-limit-label'
                            value={limit}
                            label={t('aml-limit')}
                            onChange={handleLimitChange}
                        >
                            {RECIPE_PAGE_LIMITS.map((option) => (
                                <MenuItem key={option} value={option}>{option}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </div>

                <div className='rpm-body' data-tour="rpm-body">
                    {loading ? (
                        <div className='rpm-empty'>
                            <span className='material-symbols-outlined'>hourglass_empty</span>
                            <p>{t('far-loading-recipes')}</p>
                        </div>
                    ) : recipes.length === 0 ? (
                        <div className='rpm-empty'>
                            <span className='material-symbols-outlined'>restaurant</span>
                            <p>{t('aml-no-recipes-found')}</p>
                        </div>
                    ) : (
                        <div className='rpm-grid'>
                            {recipes.map((recipe) => (
                                <div key={recipe._id} className='rpm-card-wrap'>
                                    <RecipeCard
                                        name={recipe.name?.[language] ?? recipe.name?.en ?? ''}
                                        image={sanitizeUrl(recipe.imageUrl) ?? '/images/recipe_image_placeholder.jpg'}
                                        time={recipe.preparationTime ? `${recipe.preparationTime} min` : '—'}
                                        kcal={recipe.kcal ?? 0}
                                        servings={recipe.servings ?? 1}
                                        protein={recipe.proteins ?? 0}
                                        carbs={recipe.carbs ?? 0}
                                        fat={recipe.fats ?? 0}
                                        onClick={() => handleSelect(recipe)}
                                    />
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className='rpm-footer'>
                    <span className='rpm-count'>
                        {t('aml-recipe-results-count', { total: totalCount })}
                    </span>

                    <div className='rpm-pagination'>
                        <button
                            type='button'
                            className='rpm-page-btn'
                            disabled={page === 1}
                            onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                        >
                            {t('far-prev')}
                        </button>
                        {renderPageButtons()}
                        <button
                            type='button'
                            className='rpm-page-btn'
                            disabled={page === totalPages}
                            onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                        >
                            {t('far-next')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RecipePickerModal;
